/**
 * OrderAmendmentApproval — customer reviews the admin's proposed changes and
 * approves them or cancels the order (BMF-BRD-2026-014). Rendered at the top of
 * an expanded order card when the order is PENDING_CUSTOMER_APPROVAL.
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { AlertTriangle, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import orderService from '../../services/orderService';
import { isMoneyHeld } from '../../lib/paymentLabels';

const REASON_LABELS = {
  QUALITY_REJECTED: 'Quality rejected',
  OUT_OF_STOCK: 'Out of stock',
  PARTIAL_STOCK: 'Only partial stock available',
  DAMAGED: 'Damaged in handling',
  OTHER: 'Other',
};

const formatINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const OrderAmendmentApproval = ({ order, onDecided }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [acting, setActing] = useState(null); // 'approve' | 'cancel' | null
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      const res = await orderService.getAmendment(order.id);
      if (!alive) return;
      if (res.success && res.data?.pending) setData(res.data);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [order.id]);

  const approve = async () => {
    setError('');
    setActing('approve');
    const res = await orderService.approveAmendment(order.id);
    setActing(null);
    if (res.success) onDecided?.('approved', res.data);
    else setError(res.error || 'Failed to approve');
  };

  const cancelOrder = async () => {
    setError('');
    setActing('cancel');
    const res = await orderService.cancelAmendmentOrder(order.id);
    setActing(null);
    if (res.success) onDecided?.('cancelled', res.data);
    else setError(res.error || 'Failed to cancel order');
  };

  if (loading) {
    return (
      <div className="mb-4 p-4 rounded-xl border border-amber-200 bg-amber-50 flex items-center gap-2 text-sm text-amber-800">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading approval request…
      </div>
    );
  }
  if (!data) return null;

  const { request, lines } = data;
  const revised = Number(request.proposedRevisedTotal);
  const original = Number(request.originalTotal);
  const reduction = original - revised;
  const paid = isMoneyHeld(order);

  return (
    <div className="mb-4 rounded-xl border-2 border-amber-300 bg-amber-50 overflow-hidden">
      <div className="px-4 py-3 bg-amber-100 flex items-start gap-2">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-amber-900">Some items need your approval</p>
          <p className="text-xs text-amber-800">
            Please review the changes below before packing. Approve to continue with the revised order, or cancel the order.
          </p>
        </div>
      </div>

      <div className="p-4 space-y-3">
        {lines.map((l) => {
          const isRemove = l.changeType === 'REMOVE_ITEM';
          const item = order.items.find((i) => i.orderItemId === l.orderItemId);
          const name = item?.name || `Item #${l.orderItemId}`;
          return (
            <div key={l.id} className="rounded-lg bg-white border border-amber-100 p-3">
              <p className="font-medium text-gray-800 text-sm">{name}</p>
              <p className="text-sm text-gray-700 mt-0.5">
                {isRemove
                  ? <span className="text-red-600 font-medium">Will be removed from your order</span>
                  : <>Quantity will be reduced from <span className="font-semibold">{Number(l.originalQuantity)}</span> to <span className="font-semibold">{Number(l.proposedQuantity)}</span></>}
              </p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-xs text-gray-500">
                <span>Reason: {REASON_LABELS[l.reasonCode] || l.reasonCode}</span>
                <span>Impact: <span className="text-red-600 font-medium">- {formatINR(l.amountDelta)}</span></span>
              </div>
              {l.adminNote && <p className="text-xs text-gray-500 mt-1 italic">“{l.adminNote}”</p>}
            </div>
          );
        })}

        {/* Revised total summary */}
        <div className="rounded-lg bg-white border border-amber-100 p-3">
          <div className="flex justify-between text-sm text-gray-600">
            <span>Original Total</span><span>{formatINR(original)}</span>
          </div>
          <div className="flex justify-between text-sm text-gray-600">
            <span>Reduction</span><span className="text-red-600">- {formatINR(reduction)}</span>
          </div>
          {/* isMoneyHeld, not `paymentStatus === 'paid'` — a SECOND amendment
              arrives on an order the first one already moved to
              'partial_refund'. That order is still paid (for its revised total),
              so this reduction is a further refund, not a lowered payable
              (BMF-159). */}
          <div className="flex justify-between text-sm font-semibold text-gray-800 mt-1 border-t border-gray-100 pt-1">
            <span>Revised {paid ? 'Refund' : 'Payable'}</span>
            <span>{paid ? formatINR(reduction) : formatINR(revised)}</span>
          </div>
          {paid && reduction > 0 && (
            <p className="text-[11px] text-gray-400 mt-1">A refund of {formatINR(reduction)} will be initiated on approval.</p>
          )}
        </div>

        {error && (
          <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {error}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-2 pt-1">
          <button
            onClick={approve}
            disabled={!!acting}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium bg-[#548B2E] hover:bg-[#456f26] disabled:opacity-50"
          >
            {acting === 'approve' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
            Approve
          </button>
          <button
            onClick={cancelOrder}
            disabled={!!acting}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50"
          >
            {acting === 'cancel' ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
            Cancel Order
          </button>
        </div>
      </div>
    </div>
  );
};

OrderAmendmentApproval.propTypes = {
  order: PropTypes.object.isRequired,
  onDecided: PropTypes.func,
};

export default OrderAmendmentApproval;
